import { AddIcon } from '@chakra-ui/icons';
import {
  Button,
  Center,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CompanyUsersTab,
  CurrentUsageTab,
  UsageHistoryDetailTab,
  UsageHistoryTab,
} from 'components/CompanyDetail';
import {
  useOrganizationUsageData,
  useOrganizationUsers,
  useUpdateOrganizationUser,
} from 'queries/useOrganization';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Organization } from 'services/organization';

interface CompanyDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyData: Organization | null;
}

const CompanyDetailModal: React.FC<CompanyDetailModalProps> = ({
  isOpen,
  onClose,
  companyData,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const queryClient = useQueryClient();
  const _toast = useToast();

  const bgModal = useColorModeValue('white', 'gray.800');
  const colorText = useColorModeValue('gray.800', 'white');

  // Handle close modal and clear cache
  const handleClose = () => {
    // Invalidate cache for this company's data
    if (companyData?.id) {
      queryClient.invalidateQueries({
        queryKey: ['organizations', 'detail', companyData.id, 'usageData'],
      });
      queryClient.invalidateQueries({
        queryKey: ['organizations', 'detail', companyData.id, 'users'],
      });
    }
    // Also invalidate organizations list to ensure user count is updated
    queryClient.invalidateQueries({
      queryKey: ['organizations', 'list'],
    });
    onClose();
  };

  // Handle user update using organization service
  const updateUserMutation = useUpdateOrganizationUser();
  const handleUpdateUser = async (userId: string, userData: any) => {
    console.log('CompanyDetailModal.handleUpdateUser called with:', { userId, userData });

    try {
      // Find the current user to get the email
      const currentUser = users?.find((user: any) => user.id === userId);
      if (!currentUser) {
        throw new Error('User not found');
      }

      const result = await updateUserMutation.mutateAsync({
        orgId: companyData!.id,
        userId,
        userData: {
          first_name: userData.first_name,
          last_name: userData.last_name,
          role: userData.role,
          email: currentUser.email, // Include email from current user data
          locale: userData.locale || currentUser.locale || 'en', // Include locale
        },
      });

      console.log('CompanyDetailModal.handleUpdateUser response:', result);

      // Additional validation - check if the result indicates success
      if (result && result.success === false) {
        const errorMessage = result.message || 'User update failed';
        console.error(
          'CompanyDetailModal.handleUpdateUser failed with success: false:',
          errorMessage
        );
        throw new Error(errorMessage);
      }

      console.log('CompanyDetailModal.handleUpdateUser successful:', result);
      // Success toast will be handled in CompanyUsersTab after successful API call
    } catch (error) {
      console.error('CompanyDetailModal.handleUpdateUser failed:', error);
      // Re-throw the error so it can be caught by CompanyUsersTab
      throw error;
    }
  };

  // Fetch usage data and users data
  const {
    data: usageData,
    isLoading: isLoadingUsage,
    error: _usageError,
  } = useOrganizationUsageData(companyData?.id || '');
  const {
    data: users,
    isLoading: isLoadingUsers,
    error: _usersError,
  } = useOrganizationUsers(companyData?.id || '');

  if (!companyData) {
    return null;
  }

  // Show loading state
  if (isLoadingUsage || isLoadingUsers) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size='6xl'>
        <ModalOverlay />
        <ModalContent bg={bgModal}>
          <ModalHeader color={colorText}>
            {t('managementSystem.companyDetail.title', { company: companyData.name || 'Company' })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Center py={20}>
              <Spinner color='blue.500' size='xl' />
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='6xl'>
      <ModalOverlay />
      <ModalContent maxH='90vh' bg={bgModal}>
        <ModalHeader color={colorText}>
          {t('managementSystem.companyDetail.title', { company: companyData.name || 'Company' })}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody maxH='calc(90vh - 120px)' p={0}>
          <Tabs
            h='100%'
            colorScheme='blue'
            index={activeTab}
            onChange={setActiveTab}
            variant='enclosed'
          >
            <TabList>
              <Tab>{t('managementSystem.companyDetail.currentUsage')}</Tab>
              <Tab>{t('managementSystem.companyDetail.usageHistory')}</Tab>
              <Tab>{t('managementSystem.companyDetail.usageHistoryDetail')}</Tab>
              <Tab>{t('managementSystem.companyDetail.companyUsers')}</Tab>
            </TabList>

            {/* Add User Button - Always visible when on Company Users tab */}
            {activeTab === 3 && (
              <Flex justify='flex-end' px={4} pt={2}>
                <Button
                  colorScheme='blue'
                  isDisabled={isEditingUser}
                  leftIcon={<AddIcon />}
                  onClick={() => {
                    const event = new CustomEvent('triggerAddUser');
                    window.dispatchEvent(event);
                  }}
                  size='sm'
                >
                  {t('managementSystem.companyDetail.addUser')}
                </Button>
              </Flex>
            )}

            <TabPanels overflow='auto' h='calc(100% - 60px)'>
              {/* Current Usage Tab */}
              <TabPanel h='100%' p={4}>
                <CurrentUsageTab usageData={usageData} limitation={companyData.limitation} />
              </TabPanel>

              {/* Usage History Tab */}
              <TabPanel h='100%' p={4}>
                <UsageHistoryTab usageData={usageData} />
              </TabPanel>

              {/* Usage History Detail Tab */}
              <TabPanel h='100%' p={4}>
                <UsageHistoryDetailTab usageData={usageData} />
              </TabPanel>

              {/* Company Users Tab */}
              <TabPanel h='100%' p={4}>
                <CompanyUsersTab
                  users={users}
                  organizationId={companyData.id}
                  onUpdateUser={handleUpdateUser}
                  onEditingStateChange={setIsEditingUser}
                  isNotSuperAdmin={false}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CompanyDetailModal;
